import './databasesAndPolicies.less'
import {
  ACTION_DATABASES_AND_POLICIES,
  DatabasesAndPoliciesParams,
  fetchDatabasesAndPolicies,
  reset
} from './databasesAndPoliciesSlice'
import { RootState } from '../../rootReducer'
import useUrlContext from '../../hooks/useUrlContext'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_DATABASES,
  URL_POLICIES,
  URL_RULE_SET
} from '../../constants'
import { getShowLoader } from '../../reducers/requestReducer'
import LoaderView from '../../components/Loader/loader'
import SvgIconPolicies from '../../assets/inlineSvg/icon-policies.svg'
import { getUpdatedUrl } from '../../utils/urlUtil'
import AlertErrorIcon from '../../assets/inlineSvg/alert-error-icon-oct.svg'
import { mockDatabasesAndPoliciesWidgetData } from '../dashboards/renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../dashboards/mainDashboardv2/constants'
import { getDataSourceBasePath } from '../../utils/dataSourceUtil'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { HardDrives as SvgHardDrives } from 'phosphor-react'
import { Link } from 'react-router-dom'
import { Typography } from '@lightbeamai/design-system'

interface OwnProps {
  useMockData?: boolean
  skippedDbs?: { database: string }[]
  className?: string
  widgetDatasourceId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
  widgetDashboardId?: string
}

interface StateProps {
  databasesCount?: number
  policiesCount?: number
  showLoader: boolean
}

interface DispatchProps {
  fetchDatabasesAndPolicies: (
    params: DatabasesAndPoliciesParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
  reset: () => void
}

type DatabasesAndPoliciesComponentProps = OwnProps & StateProps & DispatchProps

const DatabasesAndPolicies = (props: DatabasesAndPoliciesComponentProps): React.ReactElement => {
  const {
    databasesCount = 0,
    policiesCount = 0,
    className = '',
    showLoader,
    skippedDbs = [],
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDatasourceType,
    fetchDatabasesAndPolicies,
    reset
  } = props

  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = widgetDatasourceId || context.contextFilters[DATA_SOURCE_ID]

  useEffect(() => {
    if (useMockData) return
    if (datasourceId) {
      fetchDatabasesAndPolicies({
        datasourceId,
        [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
      })
    }
  }, [datasourceId, fetchDatabasesAndPolicies, useMockData])

  useEffect(() => () => reset(), [reset])

  const pathBaseString = getDataSourceBasePath(
    widgetDatasourceType,
    datasourceId,
    context.pathname,
    false
  )

  const linkDatabases = getUpdatedUrl({
    pathName: pathBaseString + URL_DATABASES,
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType })
    }
  })
  const linkPolicies = getUpdatedUrl({
    pageName: URL_RULE_SET,
    pathName: URL_POLICIES + URL_RULE_SET,
    pageQueryParams: {
      [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list
    },
    resetPageQuery: true,
    resetQuery: true
  })

  return (
    <div
      className={`databases-and-policies ${className}`}
      data-test-id="databasesandpolicies-databases-and-policies"
    >
      <Typography className="font-600 widget-header" as="div">
        {intl.formatMessage({ id: 'dataSources.widget.overview.title' })}
      </Typography>
      <div className="database-count">
        <Link to={linkDatabases} className="widget">
          <div className="icon-box">
            <SvgHardDrives size={24} weight="thin" color="var(--color-text-secondary)" />
          </div>
          <div className="xs-pt-8">
            <Typography variant="basePlus2" color="primary" className="font-600">
              {intl.formatNumber(databasesCount, { notation: 'compact' })}
            </Typography>
          </div>
          <div>
            <Typography variant="baseMinus2" color="secondary" className="font-500">
              {intl.formatMessage({ id: 'databasesAndPolicies.databases' })}
            </Typography>
          </div>
          {skippedDbs && skippedDbs.length > 0 && (
            <div className="skipped-dbs">
              <Typography as="span" variant="baseMinus2" color="error">
                {intl.formatMessage(
                  { id: 'databasesAndPolicies.skippedDbs' },
                  { count: skippedDbs.length }
                )}
              </Typography>
              <AlertErrorIcon />
            </div>
          )}
        </Link>
        <div className="database-divider" />
        <Link to={linkPolicies} className="widget">
          <div className="icon-box">
            <SvgIconPolicies />
          </div>
          <div className="xs-pt-8">
            <Typography variant="basePlus2" color="primary" className="font-600">
              {intl.formatNumber(policiesCount, { notation: 'compact' })}
            </Typography>
          </div>
          <div>
            <Typography variant="baseMinus2" color="secondary" className="font-500">
              {intl.formatMessage({ id: 'databasesAndPolicies.policies' })}
            </Typography>
          </div>
        </Link>
      </div>
      <LoaderView showLoader={showLoader} size="small" />
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  databasesCount: ownProps?.useMockData
    ? mockDatabasesAndPoliciesWidgetData.databasesCount
    : state.databasesAndPolicies[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ]?.databasesCount,
  policiesCount: ownProps?.useMockData
    ? mockDatabasesAndPoliciesWidgetData.policiesCount
    : state.databasesAndPolicies[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ]?.policiesCount,
  showLoader: getShowLoader(ACTION_DATABASES_AND_POLICIES)
})

const mapDispatchToProps: DispatchProps = {
  fetchDatabasesAndPolicies,
  reset
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(DatabasesAndPolicies)
